const PEP_LOGIN_UI_EVENT_LABELS = {
  PEP_LOGIN_UI_PROMPT_REQUESTED: 'Login requested',
  PEP_LOGIN_UI_MODAL_OPENED: 'Login screen shown',
  PEP_LOGIN_UI_MODAL_ACK: 'Login screen confirmed',
  PEP_LOGIN_UI_FALLBACK_OPENED: 'Fallback login window',
  PEP_LOGIN_UI_SKIPPED_DUPLICATE: 'Prompt skipped (already open)',
  PEP_LOGIN_UI_SKIPPED_CIRCUIT_BREAKER: 'Prompt skipped (too soon)',
  PEP_LOGIN_UI_SUBMITTED: 'Credentials submitted',
  PEP_LOGIN_UI_SUCCEEDED: 'Sign-in succeeded',
  PEP_LOGIN_UI_FAILED: 'Sign-in failed',
  PEP_LOGIN_UI_CANCELLED: 'Sign-in cancelled',
  PEP_LOGIN_UI_SNOOZED: 'Sign-in postponed',
  PEP_LOGIN_UI_CLOSED: 'Login screen closed',
  PEP_LOGIN_OS_PROMPT_SHOWN: 'PEP login notification',
  PEP_LOGIN_RECOVERY_INCOMPLETE: 'PEP login incomplete recovery'
}

const ENTRY_SOURCE_MESSAGES = {
  dashboard_test_login:
    'The app requested the Hilton PEP login screen because the user chose Test Login from the dashboard.',
  after_auto_login_failure:
    'The app requested the Hilton PEP login screen because automatic PEP sign-in failed.',
  pep_session_recovery:
    'The app requested the Hilton PEP login screen because the PEP session expired and the user needed to sign in again.'
}

const LEGACY_CODE_IN_MESSAGE = {
  PEP_LOGIN_UI_PROMPT_REQUESTED: 'requested the Hilton PEP login screen',
  PEP_LOGIN_UI_MODAL_OPENED: 'login screen was shown',
  PEP_LOGIN_UI_MODAL_ACK: 'login screen is open',
  PEP_LOGIN_UI_FALLBACK_OPENED: 'login screen opened',
  PEP_LOGIN_UI_SUBMITTED: 'submitted Hilton PEP credentials',
  PEP_LOGIN_UI_SUCCEEDED: 'sign-in completed successfully',
  PEP_LOGIN_UI_FAILED: 'sign-in failed',
  PEP_LOGIN_UI_CANCELLED: 'closed the Hilton PEP login screen without',
  PEP_LOGIN_UI_SNOOZED: 'postponed the Hilton PEP login screen',
  PEP_LOGIN_UI_CLOSED: 'login flow ended'
}

/** Human label for a snooze duration, e.g. 30 → "30 minutes", 120 → "2 hours". */
function formatSnoozeDuration(rawMinutes) {
  const minutes = parseInt(rawMinutes, 10)
  if (!Number.isFinite(minutes) || minutes <= 0) {
    return 'a while'
  }
  if (minutes < 60) {
    return `${minutes} minutes`
  }
  const hours = minutes / 60
  const rounded = Number.isInteger(hours) ? hours : Math.round(hours * 10) / 10
  return rounded === 1 ? '1 hour' : `${rounded} hours`
}

function parseLegacyTelemetryFields(raw) {
  const fields = {}
  if (!raw || typeof raw !== 'string') {
    return fields
  }
  const pattern = /(\w+)=([^\s]+)/g
  let match = pattern.exec(raw)
  while (match != null) {
    fields[match[1]] = match[2]
    match = pattern.exec(raw)
  }
  return fields
}

function legacyMessageForCode(eventCode, fields) {
  const step = fields.step != null ? ` during ${fields.step}` : ''
  const err =
    fields.error != null ? `: ${String(fields.error).replace(/^error=/, '')}` : ''

  switch (eventCode) {
    case 'PEP_LOGIN_UI_PROMPT_REQUESTED':
      return (
        ENTRY_SOURCE_MESSAGES[fields.entrySource] ||
        'The app requested the Hilton PEP login screen.'
      )
    case 'PEP_LOGIN_UI_MODAL_OPENED':
      return 'The Hilton PEP login screen was shown in the dashboard.'
    case 'PEP_LOGIN_UI_MODAL_ACK':
      return 'The dashboard confirmed the Hilton PEP login screen is open.'
    case 'PEP_LOGIN_UI_FALLBACK_OPENED':
      return 'The dashboard did not show the login screen in time, so Hilton PEP sign-in opened in a separate window.'
    case 'PEP_LOGIN_UI_SKIPPED_DUPLICATE':
      return 'The login screen was not shown because a Hilton PEP sign-in prompt is already open.'
    case 'PEP_LOGIN_UI_SKIPPED_CIRCUIT_BREAKER': {
      const reason =
        fields.reason != null ? ` (${String(fields.reason).replace(/^reason=/, '')})` : ''
      return `The login screen was not shown to avoid repeated prompts after a recent automatic sign-in failure${reason}.`
    }
    case 'PEP_LOGIN_UI_SUBMITTED':
      return 'The user submitted Hilton PEP credentials.'
    case 'PEP_LOGIN_UI_SUCCEEDED':
      return 'Hilton PEP sign-in completed successfully.'
    case 'PEP_LOGIN_UI_FAILED':
      return `Hilton PEP sign-in failed${step}${err}.`
    case 'PEP_LOGIN_UI_CANCELLED':
      return 'The user closed the Hilton PEP login screen without completing sign-in.'
    case 'PEP_LOGIN_UI_SNOOZED':
      return `A user postponed the Hilton PEP login screen for ${formatSnoozeDuration(
        fields.snoozeMinutes
      )}. Reservation reporting stays paused on this computer until they sign in or the reminder returns.`
    case 'PEP_LOGIN_UI_CLOSED':
      return 'The Hilton PEP login flow ended and the sign-in screen was closed.'
    case 'PEP_LOGIN_OS_PROMPT_SHOWN':
      return 'The desktop app showed an OS notification asking the user to sign in to Hilton PEP again.'
    case 'PEP_LOGIN_RECOVERY_INCOMPLETE': {
      const reason =
        fields.reason != null ? ` (${String(fields.reason).replace(/^reason=/, '')})` : ''
      return `Hilton PEP sign-in appeared to succeed but reporting did not resume within 30 minutes${reason}.`
    }
    default:
      return null
  }
}

/** Strips trailing technical parenthetical from new desktop messages. */
function stripTechnicalSuffix(raw) {
  const trimmed = (raw || '').trim()
  const parenIdx = trimmed.lastIndexOf(' (entrySource=')
  if (parenIdx > 0) {
    return trimmed.slice(0, parenIdx).trim()
  }
  return trimmed
}

export function pepLoginUiEventShortLabel(eventCode) {
  const code = (eventCode || '').trim()
  return PEP_LOGIN_UI_EVENT_LABELS[code] || null
}

/**
 * User-facing operational text for PepLoginUi telemetry in Health Check.
 */
export function formatPepLoginUiOperationalMessage(ev) {
  const raw = (ev.message ?? ev.Message ?? '').trim()
  const eventCode = (ev.eventCode ?? ev.EventCode ?? '').trim()

  if (raw && !raw.startsWith('entrySource=')) {
    const withoutTechnical = stripTechnicalSuffix(raw)
    if (withoutTechnical.length > 0) {
      const code = (eventCode || '').trim()
      return withoutTechnical
    }
  }

  if (eventCode) {
    const fromCode = legacyMessageForCode(eventCode, parseLegacyTelemetryFields(raw))
    if (fromCode) {
      return fromCode
    }
  }

  for (const [code, fragment] of Object.entries(LEGACY_CODE_IN_MESSAGE)) {
    if (raw.includes(code) || raw.includes(fragment)) {
      const fromCode = legacyMessageForCode(code, parseLegacyTelemetryFields(raw))
      if (fromCode) {
        return fromCode
      }
    }
  }

  if (raw) {
    return raw
  }

  return 'Hilton PEP login activity was recorded.'
}

export function isPepLoginUiTelemetryEvent(ev) {
  const type = (ev.eventType ?? ev.EventType ?? '').trim()
  const code = (ev.eventCode ?? ev.EventCode ?? '').trim()
  return (
    type === 'PepLoginUi' ||
    code.startsWith('PEP_LOGIN_UI_') ||
    code === 'PEP_LOGIN_OS_PROMPT_SHOWN' ||
    code === 'PEP_LOGIN_RECOVERY_INCOMPLETE' ||
    (ev.message ?? ev.Message ?? '').includes('PEP_LOGIN_UI_')
  )
}
